import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Globe,
  Palette,
  Mail,
  Bell,
  DollarSign,
  FileText,
  Save,
  Upload,
} from "lucide-react";

export default function SiteSettings() {
  const [siteConfig, setSiteConfig] = useState({
    // General
    siteName: "FellasCard",
    siteUrl: "https://fellas.com",
    supportEmail: "support@fellas.com",
    logoUrl: "/logo.png",
    faviconUrl: "/favicon.ico",
    
    // Features
    enableSignups: true,
    enableKYC: true,
    enableCards: true,
    enableWallet: true,
    
    // Appearance
    primaryColor: "#6366f1",
    darkMode: true,
    customCSS: "",
    
    // Email
    emailFromName: "FellasCard",
    emailFromAddress: "noreply@fellas.com",
    welcomeEmailEnabled: true,
    
    // Legal
    termsUrl: "/terms",
    privacyUrl: "/privacy",
    copyrightText: "© 2024 FellasCard. All rights reserved.",
    
    // Payment
    currency: "USD",
    minTopup: 10,
    maxTopup: 10000,
    transactionFee: 2.5,
  });

  const [saved, setSaved] = useState(false);

  const handleChange = (key, value) => {
    setSiteConfig((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    // TODO: Save settings to Supabase
    console.log("Saving site settings:", siteConfig);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h2 className="text-2xl font-bold">Site Settings</h2>
        <p className="text-muted-foreground">Configure your platform's global settings</p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="appearance">Appearance</TabsTrigger>
          <TabsTrigger value="email">Email</TabsTrigger>
          <TabsTrigger value="payment">Payment</TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                General Settings
              </CardTitle>
              <CardDescription>
                Basic information about your platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="site-name">Site Name</Label>
                  <Input
                    id="site-name"
                    value={siteConfig.siteName}
                    onChange={(e) => handleChange("siteName", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="site-url">Site URL</Label>
                  <Input
                    id="site-url"
                    type="url"
                    value={siteConfig.siteUrl}
                    onChange={(e) => handleChange("siteUrl", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="support-email">Support Email</Label>
                  <Input
                    id="support-email"
                    type="email"
                    value={siteConfig.supportEmail}
                    onChange={(e) => handleChange("supportEmail", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo-url">Logo URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="logo-url"
                      value={siteConfig.logoUrl}
                      onChange={(e) => handleChange("logoUrl", e.target.value)}
                    />
                    <Button variant="outline" size="icon">
                      <Upload className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="copyright">Copyright Text</Label>
                <Input
                  id="copyright"
                  value={siteConfig.copyrightText}
                  onChange={(e) => handleChange("copyrightText", e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Settings */}
        <TabsContent value="features" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Feature Toggles
              </CardTitle>
              <CardDescription>
                Enable or disable platform features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enable-signups">Enable User Signups</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow new users to register accounts
                  </p>
                </div>
                <Switch
                  id="enable-signups"
                  checked={siteConfig.enableSignups}
                  onCheckedChange={(checked) => handleChange("enableSignups", checked)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enable-kyc">Enable KYC Verification</Label>
                  <p className="text-sm text-muted-foreground">
                    Require identity verification for users
                  </p>
                </div>
                <Switch
                  id="enable-kyc"
                  checked={siteConfig.enableKYC}
                  onCheckedChange={(checked) => handleChange("enableKYC", checked)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enable-cards">Enable Card Management</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow users to create and manage cards
                  </p>
                </div>
                <Switch
                  id="enable-cards"
                  checked={siteConfig.enableCards}
                  onCheckedChange={(checked) => handleChange("enableCards", checked)}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="enable-wallet">Enable Wallet</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable wallet and transaction features
                  </p>
                </div>
                <Switch
                  id="enable-wallet"
                  checked={siteConfig.enableWallet}
                  onCheckedChange={(checked) => handleChange("enableWallet", checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Settings */}
        <TabsContent value="appearance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                Appearance Settings
              </CardTitle>
              <CardDescription>
                Customize the look and feel of your platform
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="primary-color">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primary-color"
                      type="color"
                      value={siteConfig.primaryColor}
                      onChange={(e) => handleChange("primaryColor", e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      value={siteConfig.primaryColor}
                      onChange={(e) => handleChange("primaryColor", e.target.value)}
                      placeholder="#6366f1"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="dark-mode">Dark Mode</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable dark theme support
                    </p>
                  </div>
                  <Switch
                    id="dark-mode"
                    checked={siteConfig.darkMode}
                    onCheckedChange={(checked) => handleChange("darkMode", checked)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="custom-css">Custom CSS</Label>
                <Textarea
                  id="custom-css"
                  placeholder="/* Add custom CSS here */"
                  value={siteConfig.customCSS}
                  onChange={(e) => handleChange("customCSS", e.target.value)}
                  className="font-mono min-h-[100px]"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Email Settings
              </CardTitle>
              <CardDescription>
                Configure email notifications and templates
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email-from-name">From Name</Label>
                  <Input
                    id="email-from-name"
                    value={siteConfig.emailFromName}
                    onChange={(e) => handleChange("emailFromName", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email-from-address">From Address</Label>
                  <Input
                    id="email-from-address"
                    type="email"
                    value={siteConfig.emailFromAddress}
                    onChange={(e) => handleChange("emailFromAddress", e.target.value)}
                  />
                </div>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="welcome-email">Welcome Email</Label>
                  <p className="text-sm text-muted-foreground">
                    Send welcome email to new users
                  </p>
                </div>
                <Switch
                  id="welcome-email"
                  checked={siteConfig.welcomeEmailEnabled}
                  onCheckedChange={(checked) =>
                    handleChange("welcomeEmailEnabled", checked)
                  }
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Settings */}
        <TabsContent value="payment" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Payment Settings
              </CardTitle>
              <CardDescription>
                Configure payment and transaction settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Input
                    id="currency"
                    value={siteConfig.currency}
                    onChange={(e) => handleChange("currency", e.target.value)}
                    placeholder="USD"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transaction-fee">Transaction Fee (%)</Label>
                  <Input
                    id="transaction-fee"
                    type="number"
                    step="0.1"
                    value={siteConfig.transactionFee}
                    onChange={(e) =>
                      handleChange("transactionFee", parseFloat(e.target.value))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="min-topup">Minimum Top-up</Label>
                  <Input
                    id="min-topup"
                    type="number"
                    value={siteConfig.minTopup}
                    onChange={(e) =>
                      handleChange("minTopup", parseInt(e.target.value))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-topup">Maximum Top-up</Label>
                  <Input
                    id="max-topup"
                    type="number"
                    value={siteConfig.maxTopup}
                    onChange={(e) =>
                      handleChange("maxTopup", parseInt(e.target.value))
                    }
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} size="lg">
          <Save className="h-4 w-4 mr-2" />
          {saved ? "Settings Saved!" : "Save All Settings"}
        </Button>
      </div>
    </div>
  );
}